import React from 'react';
import {
  Card,
  Text,
  Badge,
  Group,
  Stack,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  Button,
} from '@mantine/core';
import { IconEdit, IconTrash, IconCheck, IconClock, IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Goal } from '../../types';
import { formatDate, formatDay, goalDeadlineDay } from '../../utils/date';
import { getGoalStatusColor, getGoalStatusText } from '../../utils/statusUtils';
import { useHasFeature } from '../../store/featureFlagsStore';
import { GoalComments } from './GoalComments';

interface GoalCardProps {
  goal: Goal;
  currentUserId: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onAccept?: () => void;
  /** false — не получилось: окно остаётся открытым, комментарий не теряется. */
  onComplete?: (comment: string) => Promise<boolean> | void;
}

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  currentUserId,
  onEdit,
  onDelete,
  onAccept,
  onComplete,
}) => {
  const { t, i18n } = useTranslation();
  const [showCompleteModal, setShowCompleteModal] = React.useState(false);
  const [completionComment, setCompletionComment] = React.useState('');
  const isCommentsOn = useHasFeature('goalComments');

  // Постановщик правит и удаляет, исполнитель принимает и завершает — ровно так
  // это разводит бэкенд. Обычно это один и тот же человек (цель ставят себе), из-за
  // чего расхождение и не замечали: у чужой цели кнопки показывались постановщику,
  // а его запрос сервис отклонял.
  const isAssigner = goal.assigner?._id === currentUserId;
  const isAssignee = goal.assignee?._id === currentUserId;
  const canEdit = isAssigner && goal.status === 'pending';
  const canDelete = isAssigner && goal.status === 'pending';
  const canAccept = isAssignee && goal.status === 'pending';
  const canComplete = isAssignee && goal.status === 'accepted';

  const [isCompleting, setIsCompleting] = React.useState(false);
  const handleComplete = async () => {
    if (!completionComment.trim()) return;
    setIsCompleting(true);
    const isDone = await onComplete?.(completionComment.trim());
    setIsCompleting(false);
    if (isDone === false) return;
    setShowCompleteModal(false);
    setCompletionComment('');
  };

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <div style={{ flex: 1 }}>
              <Text fw={500} size="lg" mb={4}>
                {goal.title}
              </Text>
              <Text size="sm" c="dimmed" mb={8}>
                {goal.description}
              </Text>
              {!!goal.competencies?.length && (
                <Group gap={4} mb={8}>
                  {goal.competencies.map((c) => (
                    <Badge key={c._id} size="sm" variant="light" color="gray">
                      {c.title}
                    </Badge>
                  ))}
                </Group>
              )}
            </div>
            <Group gap="xs">
              {canEdit && (
                <Tooltip label={t('common.edit')}>
                  <ActionIcon
                    variant="light"
                    color="teal"
                    size="sm"
                    onClick={onEdit}
                    aria-label={t('goals.editGoal')}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip label={t('common.delete')}>
                  <ActionIcon
                    variant="light"
                    color="red"
                    size="sm"
                    onClick={onDelete}
                    aria-label={t('common.delete')}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          <Group gap="xs" wrap="wrap">
            <Badge
              color={getGoalStatusColor(goal)}
              variant="light"
              leftSection={goal.isOverdue ? <IconAlertTriangle size={12} /> : undefined}
            >
              {getGoalStatusText(goal, t)}
            </Badge>
          </Group>

          <Group gap="md" c="dimmed">
            <Group gap="xs">
              <Text size="xs">{t('goals.assigner')}:</Text>
              <Text size="xs" fw={500}>
                {goal.assigner?.name || '—'}
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="xs">{t('goals.assignee')}:</Text>
              <Text size="xs" fw={500}>
                {goal.assignee?.name || '—'}
              </Text>
            </Group>
          </Group>

          {goal.deadline && (
            <Group gap="xs" c="dimmed">
              <IconClock size={14} />
              <Text size="xs">
                {t('goals.deadline')}: {formatDay(goalDeadlineDay(goal), i18n.language)}
              </Text>
            </Group>
          )}

          {goal.completedAt && (
            <Group gap="xs" c="dimmed">
              <IconCheck size={14} />
              <Text size="xs">
                {t('goals.completedAt')}: {formatDate(goal.completedAt, i18n.language)}
              </Text>
            </Group>
          )}

          {goal.completionComment && (
            <Card withBorder p="xs">
              <Text size="xs" fw={500} mb={4}>
                {t('goals.completionComment')}:
              </Text>
              <Text size="xs" c="dimmed">
                {goal.completionComment}
              </Text>
            </Card>
          )}

          {isCommentsOn && <GoalComments goal={goal} />}

          <Group gap="xs" mt="sm">
            {canAccept && (
              <Button
                size="xs"
                variant="light"
                color="teal"
                leftSection={<IconCheck size={14} />}
                onClick={onAccept}
              >
                {t('goals.accept')}
              </Button>
            )}
            {canComplete && (
              <Button
                size="xs"
                variant="light"
                color="green"
                leftSection={<IconCheck size={14} />}
                onClick={() => setShowCompleteModal(true)}
              >
                {t('goals.complete')}
              </Button>
            )}
          </Group>
        </Stack>
      </Card>

      <Modal
        opened={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title={t('goals.completeGoal')}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('goals.completionCommentRequired')}
          </Text>
          <Textarea
            label={t('goals.completionComment')}
            placeholder={t('goals.completionCommentPlaceholder')}
            value={completionComment}
            onChange={(e) => setCompletionComment(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setShowCompleteModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleComplete}
              disabled={!completionComment.trim()}
              loading={isCompleting}
            >
              {t('goals.complete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
